import React, { useState, useMemo } from "react";
import {
  FaSearch,
  FaChevronDown,
  FaArrowRight,
  FaMapMarkedAlt,
} from "react-icons/fa";

const zones = [
  {
    id: 1,
    name: "North Central",
    target: 2000000,
    registered: 1480000,
    activeRegistrars: 2100,
    achievement: 74,
    updatedAt: "10:45 AM WAT",
  },
  {
    id: 2,
    name: "South West",
    target: 2500000,
    registered: 2125000,
    activeRegistrars: 3250,
    achievement: 85,
    updatedAt: "10:42 AM WAT",
  },
  {
    id: 3,
    name: "North East",
    target: 1800000,
    registered: 810000,
    activeRegistrars: 1200,
    achievement: 20,
    updatedAt: "10:40 AM WAT",
  },
  {
    id: 4,
    name: "South South",
    target: 1900000,
    registered: 1140000,
    activeRegistrars: 1850,
    achievement: 24,
    updatedAt: "10:44 AM WAT",
  },
  {
    id: 5,
    name: "North West",
    target: 2200000,
    registered: 1584000,
    activeRegistrars: 2600,
    achievement: 72,
    updatedAt: "10:41 AM WAT",
  },
  {
    id: 6,
    name: "South East",
    target: 1600000,
    registered: 1120000,
    activeRegistrars: 1500,
    achievement: 45,
    updatedAt: "10:38 AM WAT",
  },
];

const SORT_OPTIONS = ["Alphabetical", "Achievement", "Registered"];

function getStatus(achievement) {
  if (achievement >= 70) return "HIGH PERFORMANCE";
  if (achievement >= 40) return "ON TRACK";
  return "ATTENTION REQUIRED";
}

const STATUS_CONFIG = {
  "HIGH PERFORMANCE": {
    color: "#006B3F",
    badgeBg: "#e8f5e9",
    iconBg: "#006B3F1A",
  },
  "ON TRACK": { color: "#d97706", badgeBg: "#fef3c7", iconBg: "#fef3c7" },
  "ATTENTION REQUIRED": {
    color: "#dc2626",
    badgeBg: "#fee2e2",
    iconBg: "#fee2e2",
  },
};

function ZoneCard({ zone, onViewStates }) {
  const status = getStatus(zone.achievement);
  const { color, badgeBg, iconBg } = STATUS_CONFIG[status];

  return (
    <div className="flex flex-col h-full gap-1">
      <div
        className="bg-white rounded-xl border shadow-sm flex flex-col"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="p-4 flex-1">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className="rounded-lg p-2 flex items-center justify-center"
                style={{ background: iconBg }}
              >
                <FaMapMarkedAlt style={{ color, fontSize: "20px" }} />
              </div>
              <span
                className="font-bold text-gray-900"
                style={{ fontSize: "18px" }}
              >
                {zone.name}
              </span>
            </div>
            <span
              className=" font-bold"
              style={{
                color,
                background: badgeBg,
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "10px",
              }}
            >
              {status}
            </span>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-y-4 mb-1">
            <div>
              <p
                className="font-bold mb-1 mt-0"
                style={{ color: "#3E4A41", fontSize: "10px" }}
              >
                TARGET
              </p>
              <p
                className="font-bold text-gray-900 mb-0 mt-0"
                style={{ fontSize: "16px" }}
              >
                {zone.target.toLocaleString()}
              </p>
            </div>
            <div>
              <p
                className="font-bold mb-1 mt-0"
                style={{ color: "#3E4A41", fontSize: "10px" }}
              >
                REGISTERED
              </p>
              <p
                className="font-bold mb-0 mt-0"
                style={{ fontSize: "16px", color }}
              >
                {zone.registered.toLocaleString()}
              </p>
            </div>
            <div>
              <p
                className="font-bold mb-1 mt-0"
                style={{ color: "#3E4A41", fontSize: "10px" }}
              >
                ACTIVE REGISTRARS
              </p>
              <p
                className="font-bold text-gray-900 mb-0 mt-0"
                style={{ fontSize: "16px" }}
              >
                {zone.activeRegistrars.toLocaleString()}
              </p>
            </div>
            <div>
              <p
                className="font-bold mb-1 mt-0"
                style={{ color: "#3E4A41", fontSize: "10px" }}
              >
                ACHIEVEMENT
              </p>
              <p
                className="font-bold mb-0 mt-0"
                style={{ fontSize: "16px", color }}
              >
                {zone.achievement}%
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-2">
            <p className="text-xs text-gray-500 mb-2 mt-0">
              Registration Progress
            </p>
            <div className="h-2 rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${zone.achievement}%`,
                  background: color,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>

          <p className="text-xs text-gray-400 text-right mb-0 mt-2">
            Updated: {zone.updatedAt}
          </p>
        </div>
      </div>
      {/* View States button — switches tab and pre-filters by this zone */}
      <button
        onClick={() => onViewStates(zone.name)}
        className="w-full flex items-center justify-center gap-2 py-3 border-t text-sm font-semibold"
        style={{
          color: "#01903C",
          background: "transparent",
          cursor: "pointer",
          borderRadius: "8px",
          border: "2px solid #D4E9D8",
        }}
      >
        View States <FaArrowRight style={{ fontSize: "12px" }} />
      </button>
    </div>
  );
}

export default function GeopoliticalZones({ onViewStates }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Alphabetical");

  const filtered = useMemo(() => {
    let list = zones.filter((z) =>
      z.name.toLowerCase().includes(search.toLowerCase()),
    );
    if (sort === "Alphabetical")
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "Achievement")
      list = [...list].sort((a, b) => b.achievement - a.achievement);
    if (sort === "Registered")
      list = [...list].sort((a, b) => b.registered - a.registered);
    return list;
  }, [search, sort]);

  return (
    <div>
      {/* Search + Sort */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-5">
        <div
          className="flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-lg px-4"
          style={{ height: "56px" }}
        >
          <FaSearch
            style={{ color: "#9ca3af", fontSize: "14px", flexShrink: 0 }}
          />
          <input
            type="text"
            placeholder="Search Zone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 text-sm text-gray-700 bg-transparent"
            style={{ border: "none", outline: "none", height: "100%" }}
          />
        </div>

        <div
          className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4"
          style={{ height: "56px", minWidth: "200px" }}
        >
          <span className="text-sm text-gray-500 whitespace-nowrap">
            Sort By:
          </span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="flex-1 text-sm font-medium text-gray-700 bg-transparent"
            style={{
              border: "none",
              outline: "none",
              cursor: "pointer",
              appearance: "none",
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <FaChevronDown
            style={{
              color: "#6b7280",
              fontSize: "12px",
              pointerEvents: "none",
            }}
          />
        </div>
      </div>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {filtered.map((zone) => (
            <ZoneCard key={zone.id} zone={zone} onViewStates={onViewStates} />
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm text-center py-12 mb-0">
          No zones match your search.
        </p>
      )}
    </div>
  );
}
